// @flow
/* eslint-disable class-methods-use-this */
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { compose } from 'ramda';
import { connect } from 'react-redux';
import Waypoint from 'react-waypoint';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import LegacyLink from '../components/Common/LegacyLink';
import HomePhoneWebp from '../../static/images/home_phone.webp';
import HomePhoneSmallWebp from '../../static/images/home_phone_small.webp';
import HomePhonePng from '../../static/images/home_phone.png';
import ConnectedSearchForm from '../components/Search/Form/SearchForm';
import ThreeFeatureSlider from '../components/Common/ThreeFeatureSlider';
import AppFeature from '../components/Common/AppFeature';
import ContentModule from '../components/Common/ContentModule';
import Media from '../components/Common/Media';
import withAnalytics from '../components/Decorators/withAnalytics';
import pmeFind from '../../static/images/find_parking.png';
import pmeReserve from '../../static/images/reserve.png';
import pmePark from '../../static/images/park_car.png';
import * as actions from '../stores/search';
import { updateUI as updateUIAction } from '../stores/ui';
import HomeNotificationBar from '../components/HomeNotificationBar';
import FancyBanner from '../components/UI/FancyBanner';
import getMonthlyDuration from '../helpers/getMonthlyDuration';
import { defaultSearchDates } from '../helpers/date';
import usePageView from '../hooks/usePageview';

import '../../static/scss/pages/home.scss';
import '../../static/scss/components/hero.scss';

const ReviewsPanel = loadable(
  () =>
    import(/* webpackChunkName: "component-reviews-panel" */ '../components/Common/Review/ReviewsPanel'),
  {
    ssr: false
  }
);

type Props = {
  ui?: any,
  updateUI?: () => void,
  location: {
    search: string
  },
  setSearchInitialiser: () => void
};

export const Home = ({
  ui = {},
  updateUI = () => {},
  location,
  setSearchInitialiser
}: Props) => {
  const monthlyDuration = getMonthlyDuration(location.search);
  const [dates, setDates] = useState({
    ...defaultSearchDates,
    monthlyDuration
  });

  usePageView('homepage');

  const dateChangeHandler = newDates => {
    const datesState = {
      arriving: newDates[0].arriving.toDate(),
      leaving: newDates[0].leaving ? newDates[0].leaving.toDate() : null,
      monthlyDuration: newDates[0].monthlyDuration || null
    };

    setDates(datesState);
  };

  return (
    <div className="p-home">
      <Helmet
        title="JustPark - The Parking App | Find parking in seconds"
        titleTemplate="%s"
      >
        <link rel="canonical" href="https://www.justpark.com/" />
      </Helmet>
      <FancyBanner />
      <Waypoint
        onEnter={() => {
          updateUI({ headerShouldShowCTA: false });
        }}
        onLeave={() => {
          updateUI({ headerShouldShowCTA: true });
        }}
      >
        <ContentModule baseClassName="hero" modifier="home">
          <HomeNotificationBar />
          <div className="c-hero-content">
            <div className="c-hero-content__clear">
              <div className="c-hero__phone">
                <picture>
                  <source
                    srcSet={HomePhoneSmallWebp}
                    type="image/webp"
                    media="(max-width: 375px)"
                  />
                  <source srcSet={HomePhoneWebp} type="image/webp" />
                  <source srcSet={HomePhonePng} type="image/png" />
                  <img
                    className="c-hero__phone-img"
                    src={HomePhonePng}
                    alt="Mobile Phone"
                  />
                </picture>
              </div>
              <div className="c-hero-content__inner">
                <h1>
                  Find parking in <span>seconds</span>
                </h1>
                <p className="mobile-hidden">
                  Choose from millions of available spaces, or reserve your
                  space in advance. Join over 3.5 million drivers enjoying easy
                  parking.
                </p>
                <p className="desktop-hidden">
                  Choose from millions of spaces. <br />
                  Trusted by 3.5 million drivers.
                </p>
                <ConnectedSearchForm
                  placeholder="Where do you want to park?"
                  modifier="single"
                  cta="Show parking spaces"
                  highlight={ui.animateSearchForm}
                  location={location}
                  allowScroll
                  noSubmit
                  disableDefaultDates
                  tabTheme="dark"
                  onSubmitCallback={query => {
                    setSearchInitialiser(
                      'autosuggest_searched',
                      query.q,
                      dates
                    );
                  }}
                  dates={dates}
                  onDateChange={dateChangeHandler}
                />
              </div>
            </div>
          </div>
        </ContentModule>
      </Waypoint>
      <ThreeFeatureSlider
        modifier="parking-made-easy"
        callback={() => {
          updateUI({ animateSearchForm: true });
          if (__CLIENT__) {
            window.scroll(0, 0);
          }
        }}
      >
        <Media modifier="stacked" title="Wherever, whenever" image={pmeFind}>
          <ul>
            <li>
              <span>Choose from millions of spaces across the UK</span>
            </li>
            <li>
              <span>Find your best option for every car journey</span>
            </li>
          </ul>
        </Media>
        <Media modifier="stacked" title="Peace of mind" image={pmeReserve}>
          <ul>
            <li>
              <span>
                View information on availability, price and restrictions
              </span>
            </li>
            <li>
              <span>Reserve in advance at over 45,000+ locations</span>
            </li>
          </ul>
        </Media>
        <Media modifier="stacked" title="Seamless experience" image={pmePark}>
          <ul>
            <li>
              <span>Pay for JustPark spaces via the app or website</span>
            </li>
            <li>
              <span>Follow easy directions and access instructions</span>
            </li>
          </ul>
        </Media>
      </ThreeFeatureSlider>
      <AppFeature />
      <ContentModule baseClassName="hero" modifier="rent-space">
        <div className="c-hero-content">
          <div className="c-hero-content__clear">
            <div className="c-hero-content__inner">
              <h2>Rent out your parking space</h2>
              <p>
                Make easy tax free money by renting out your parking space.
                It&lsquo;s free to list and only takes a few minutes to get up
                and running.
              </p>
              <LegacyLink className="bt--primary" to="about/rent-your-space/">
                Learn how to earn today
              </LegacyLink>
            </div>
          </div>
        </div>
      </ContentModule>
      <ReviewsPanel modifier="home" />
      <ContentModule baseClassName="hero" modifier="parking-management">
        <div className="c-hero-content">
          <div className="c-hero-content__clear">
            <div className="c-hero-content__inner">
              <h2>Car park management</h2>
              <p>
                Maximise yield from underused car parks and vacant land, or
                transform payments with the UK’s favourite parking app.
              </p>
              <LegacyLink className="bt--primary" to="car-park-management/">
                Learn about our solutions
              </LegacyLink>
            </div>
          </div>
        </div>
      </ContentModule>
    </div>
  );
};

const mapDispatchToProps = {
  setSearchInitialiser: actions.setSearchInitialiser,
  updateUI: updateUIAction
};

const mapStateToProps = state => ({
  ui: state.ui
});

const homeComposition = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withAnalytics({}, {})
)(Home);

export default homeComposition;
